import api from "../../../services/apiClient";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import utils from "../../../utils/utils";
import {
  assertApiSuccess,
  rowsFromResponse,
  tablesFromResponse,
} from "../../../utils/apiResponseUtils";
import { guidOrNull } from "../../../utils/guidUtils";
import {
  getSessionRoleName,
  isSessionSuperAdmin,
  isSystemAdministratorRole,
} from "../../../utils/permissionUtils";
import { fetchStaffLookup } from "../../../services/lookupService";
import {
  isEplannerGuid,
  resolveLegacyApiLocationIds,
  resolveVolunteerGuid,
  sortAppointmentHistoryRowsDescending,
  sortAppointmentsByBookingDateLatestFirst,
  uniqueAppointmentRows,
} from "./eplannerReportUtils";

const idUpper = (value) =>
  value === undefined || value === null || value === ""
    ? ""
    : String(value).toUpperCase();

/** GUID-only for uniqueidentifier SP params — never send volunteer numbers like "189". */
const guidUpperOrEmpty = (value) => {
  const text = String(value ?? "").trim();
  return isEplannerGuid(text) ? text.toUpperCase() : "";
};

/** Legacy SP style 103: DD/MM/YYYY HH:mm:ss */
export function toLegacyAppointmentDateTime(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  if (/^\d{1,2}\/\d{1,2}\/\d{4}/.test(text)) {
    const match = text.match(
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?)?/i,
    );
    if (!match) return text;
    const day = match[1].padStart(2, "0");
    const month = match[2].padStart(2, "0");
    const year = match[3];
    if (!match[4]) return `${day}/${month}/${year} 00:00:00`;
    let hour = Number(match[4]);
    const minute = match[5].padStart(2, "0");
    const second = (match[6] || "00").padStart(2, "0");
    const ampm = (match[7] || "").toUpperCase();
    if (ampm === "PM" && hour < 12) hour += 12;
    if (ampm === "AM" && hour === 12) hour = 0;
    return `${day}/${month}/${year} ${String(hour).padStart(2, "0")}:${minute}:${second}`;
  }
  const iso = text.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/,
  );
  if (iso) {
    return `${iso[3]}/${iso[2]}/${iso[1]} ${iso[4]}:${iso[5]}:${iso[6] || "00"}`;
  }
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return "";
  const day = String(parsed.getDate()).padStart(2, "0");
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const year = parsed.getFullYear();
  const hour = String(parsed.getHours()).padStart(2, "0");
  const minute = String(parsed.getMinutes()).padStart(2, "0");
  const second = String(parsed.getSeconds()).padStart(2, "0");
  return `${day}/${month}/${year} ${hour}:${minute}:${second}`;
}

function baseUserFields(userData) {
  return {
    OrganizationId: idUpper(userData?.OrganizationId),
    StaffIdRef: idUpper(userData?.StaffId),
    CreatedBy: idUpper(userData?.StaffId),
  };
}

function toIdList(values) {
  if (values == null || values === "") return null;
  if (Array.isArray(values)) {
    // Keep "" — legacy SP uses empty LocationId to match NULL-location appointments.
    const list = values.map((v) => (v == null ? "" : String(v).trim()));
    return list.length ? list : null;
  }
  const text = String(values).trim();
  return text ? [text] : null;
}

function tableRowsByNames(tables = [], names = []) {
  const wanted = new Set(names.map((name) => String(name).toLowerCase()));
  const match = tables.find((table) => wanted.has(String(table.name).toLowerCase()));
  return match?.rows ?? [];
}

function pickTableRows(tables = [], names = [], response = null, fallbackIndex = -1) {
  const named = tableRowsByNames(tables, names);
  if (named.length) return named;
  if (response) {
    const fromResponse = rowsFromResponse(response, names);
    if (fromResponse.length) return fromResponse;
  }
  if (fallbackIndex >= 0 && tables[fallbackIndex]?.rows?.length) {
    return tables[fallbackIndex].rows;
  }
  return [];
}

export async function fetchPlannerStudyBundle(userData) {
  const response = await api.post(API_ENDPOINTS.ESCHEDULE.GET_STUDY_SCHEDULE_DATA, {
    ...baseUserFields(userData),
    Action: "GetPlannerstudyListdata",
  });
  assertApiSuccess(response, "Could not load planner study filters.");
  const tables = tablesFromResponse(response);
  const data = response?.data ?? {};
  return {
    studies: pickTableRows(tables, ["StudyList", "studyList", "Studies"], response, 0),
    locations: pickTableRows(
      tables,
      ["LocationList", "locationList", "Locations"],
      response,
      2,
    ),
    groups: pickTableRows(tables, ["GroupList", "groupList", "Groups"], response, 3),
    visits: [],
    raw: data,
    response,
  };
}

/**
 * Org calendar defaults (legacy GetDefaultSetup):
 * open/close times + slot duration.
 */
/** Org default clock for all-day calendar labels (legacy AddLabelDefaultTime). */
export function parseAddLabelDefaultTime(raw) {
  const text = String(raw ?? "").trim();
  const match = text.match(/(\d{1,2}):(\d{2})/);
  if (!match) return "00:05";
  return `${String(Number(match[1])).padStart(2, "0")}:${match[2]}`;
}

export async function fetchPlannerDefaultSetup(userData) {
  const empty = {
    slotMinutes: 0,
    startTime: "",
    endTime: "",
    timeZoneId: "",
    labelDefaultTime: "00:05",
  };
  if (!userData?.OrganizationId) return empty;
  const response = await api.post(API_ENDPOINTS.EPLANNER.SET_VISIT_PLANNER, {
    ...baseUserFields(userData),
    Actiontype: "GetDefaultSetup",
  });
  assertApiSuccess(response, "Could not load planner default setup.");
  const rows = rowsFromResponse(response, ["SuccessStory", "successStory"]);
  const row = rows[0] || {};
  const slotRaw =
    row?.CalendarDefaultSlotDuration ??
    row?.calendarDefaultSlotDuration ??
    row?.AppointmentSlotDuration ??
    row?.appointmentSlotDuration ??
    0;
  const minutes = Number(slotRaw);
  return {
    slotMinutes: Number.isFinite(minutes) && minutes > 0 ? minutes : 0,
    startTime: String(
      row?.CalenderDefaultStartTime ??
        row?.calenderDefaultStartTime ??
        row?.CalendarDefaultStartTime ??
        row?.calendarDefaultStartTime ??
        row?.AppointmentStartTime ??
        "",
    ).trim(),
    endTime: String(
      row?.CalendarDefaultEndTime ??
        row?.calendarDefaultEndTime ??
        row?.AppointmentEndTime ??
        "",
    ).trim(),
    timeZoneId: String(row?.TimeZoneId ?? row?.timeZoneId ?? "").trim(),
    labelDefaultTime: parseAddLabelDefaultTime(
      row?.AddLabelDefaultTime ?? row?.addLabelDefaultTime,
    ),
  };
}

/** @deprecated use fetchPlannerDefaultSetup */
export async function fetchPlannerDefaultSlotDuration(userData) {
  const setup = await fetchPlannerDefaultSetup(userData);
  return setup.slotMinutes;
}

async function fetchVisitPlannerMetaByStudy(userData, studyId) {
  const id = idUpper(studyId);
  if (!id || !userData?.OrganizationId) return new Map();
  try {
    const response = await api.post(API_ENDPOINTS.ESCHEDULE.VISITS_PER_STUDY, {
      StudyId: id,
      OrganizationId: idUpper(userData.OrganizationId),
      CreatedBy: idUpper(userData?.StaffId),
      Action: "GetPlanVisitListNew",
    });
    assertApiSuccess(response, "Could not load visit planner meta.");
    const rows = rowsFromResponse(response, ["VisitList", "visitList"]);
    const map = new Map();
    (Array.isArray(rows) ? rows : []).forEach((row) => {
      const visitId = String(
        row?.VisitsPerStudyId ?? row?.visitsPerStudyId ?? "",
      )
        .trim()
        .toUpperCase();
      if (!visitId) return;
      const minutes = Number(row?.SlotDuration ?? row?.slotDuration);
      map.set(visitId, {
        slotDuration:
          Number.isFinite(minutes) && minutes > 0 ? minutes : 0,
        dayStartTime: String(
          row?.DayWindowStartTime ?? row?.dayWindowStartTime ?? "",
        ).trim(),
        dayEndTime: String(
          row?.DayWindowEndTime ?? row?.dayWindowEndTime ?? "",
        ).trim(),
      });
    });
    return map;
  } catch {
    return new Map();
  }
}

/** Groups + visits for selected study schedule ids (legacy MultiStudyVisitList). */
export async function fetchPlannerGroupsAndVisits(userData, studyIds) {
  const list = toIdList(studyIds);
  if (!list?.length) {
    return { groups: [], visits: [], response: null };
  }
  const response = await api.post(API_ENDPOINTS.EPLANNER.GET_CALENDAR_VIEW, {
    ...baseUserFields(userData),
    StudyIds: list,
    ActionType: "MultiStudyVisitList",
    VisitId: "",
    GroupId: "",
    StartDate: "",
    EndDate: "",
  });
  assertApiSuccess(response, "Could not load planner groups and visits.");
  let visits = rowsFromResponse(response, ["Visits", "visits", "VisitList"]);
  // MultiStudyVisitList omits SlotDuration / day window — enrich from GetPlanVisitListNew.
  if (list.length === 1 && visits.length) {
    const metaByVisit = await fetchVisitPlannerMetaByStudy(userData, list[0]);
    if (metaByVisit.size) {
      visits = visits.map((row) => {
        const visitId = String(
          row?.VisitsPerStudyId ?? row?.visitsPerStudyId ?? row?.VisitId ?? "",
        )
          .trim()
          .toUpperCase();
        const meta = metaByVisit.get(visitId);
        if (!meta) return row;
        return {
          ...row,
          SlotDuration: meta.slotDuration || row?.SlotDuration,
          DayWindowStartTime: meta.dayStartTime || row?.DayWindowStartTime,
          DayWindowEndTime: meta.dayEndTime || row?.DayWindowEndTime,
        };
      });
    }
  }
  return {
    groups: rowsFromResponse(response, ["Groups", "groups", "GroupList"]),
    visits,
    response,
  };
}

function nullIfEmpty(value) {
  if (value === undefined || value === null || String(value).trim() === "") {
    return null;
  }
  return value;
}

export function buildAppointmentQueryPayload({
  userData,
  actionType = "AppointmentListNew",
  appointmentPeriod = "TODAY",
  startPeriod = "",
  endPeriod = "",
  studyIds = null,
  locationIds = null,
  groupId = "",
  visitId = "",
  appointmentStatus = "",
  appointmentType = "",
  sortBy = "",
  orderBy = "",
  volunteerId = "",
}) {
  const period = nullIfEmpty(appointmentPeriod);
  return {
    ...baseUserFields(userData),
    StudyId: null,
    ActionType: actionType,
    // Keep ALLTIME as-is (do not coerce to TODAY). Empty → TODAY for other screens.
    AppointmentPeriod: period || "TODAY",
    StartPeriod: nullIfEmpty(startPeriod),
    EndPeriod: nullIfEmpty(endPeriod),
    StudyIds: toIdList(studyIds),
    LocationIds: toIdList(locationIds),
    GroupId: nullIfEmpty(groupId),
    VisitId: nullIfEmpty(visitId),
    AppointmentStatus: nullIfEmpty(appointmentStatus),
    AppointmentType: nullIfEmpty(appointmentType),
    SortBy: nullIfEmpty(sortBy),
    OrderBy: nullIfEmpty(orderBy),
    VolunteerId: nullIfEmpty(volunteerId),
  };
}

/** Legacy SP has no ALLTIME — map to a wide Custom range using MM/DD/YYYY. */
function withLegacyAllTimePayload(payload) {
  const period = String(payload?.AppointmentPeriod || "").toUpperCase();
  if (period !== "ALLTIME") return payload;
  return {
    ...payload,
    AppointmentPeriod: "Custom",
    StartPeriod: "01/01/2000",
    EndPeriod: "12/31/2099",
  };
}

function withLegacyLocationPayload(payload, locationFilter) {
  let next = withLegacyAllTimePayload(payload);
  if (!locationFilter) return next;
  const legacyIds = resolveLegacyApiLocationIds(
    locationFilter.selectedIds,
    locationFilter.optionIds,
  );
  return {
    ...next,
    LocationIds: toIdList(legacyIds),
  };
}

function appointmentRowsFromResponse(response) {
  const preferred = ["AppointmentList", "appointmentList"];
  const tables = tablesFromResponse(response);
  const fromTables = pickTableRows(tables, preferred, response, 0);
  let rows = fromTables;
  if (!rows.length) {
    const data = response?.data ?? response?.Data ?? response;
    if (data && typeof data === "object") {
      for (const key of preferred) {
        if (Array.isArray(data[key])) {
          rows = data[key];
          break;
        }
      }
    }
  }
  if (!rows.length) rows = rowsFromResponse(response, preferred);
  // One row per volunteer+study+visit+group (latest creation). Drop Cancelled latest
  // so older Confirmed siblings do not resurface after cancel.
  return sortAppointmentsByBookingDateLatestFirst(
    uniqueAppointmentRows(rows, { dropCancelledLatest: true }),
  );
}

export async function fetchAppointments(payload, locationFilter = null) {
  let response;
  let usedFast = false;
  try {
    response = await api.post(
      API_ENDPOINTS.EPLANNER.GET_APPOINTMENT_FAST,
    payload,
  );
    if (!utils.isApiSuccess(response)) {
      throw new Error(
        utils.getApiResponseMessage(response, "Could not load appointments."),
      );
    }
    usedFast = true;
  } catch (fastError) {
    response = await api.post(
      API_ENDPOINTS.EPLANNER.GET_APPOINTMENT,
      withLegacyLocationPayload(payload, locationFilter),
    );
  assertApiSuccess(response, "Could not load appointments.");
    if (process.env.NODE_ENV === "development") {
      console.warn(
        "GetAppointmentFast unavailable, used GetAppointment fallback.",
        fastError,
      );
    }
  }
  return {
    rows: appointmentRowsFromResponse(response),
    response,
    usedFast,
  };
}

function toAppointmentListDate(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const d = String(value.getDate()).padStart(2, "0");
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const y = value.getFullYear();
    return `${d}/${m}/${y}`;
  }
  const text = String(value || "").trim();
  if (!text) return "";
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const parsed = new Date(text);
  if (!Number.isNaN(parsed.getTime())) return toAppointmentListDate(parsed);
  return "";
}

function sameAppointmentId(left, right) {
  return (
    String(left || "")
      .trim()
      .toUpperCase() ===
    String(right || "")
      .trim()
      .toUpperCase()
  );
}

/**
 * Load one full AppointmentListNew row (VolIdRef, contact, screening, etc.)
 * Used when occupancy/calendar opens a thin stub that only has AppointmentId.
 */
export async function fetchAppointmentListRowById({
  userData,
  appointmentId,
  studyId = "",
  startTime = "",
}) {
  const id = String(appointmentId || "").trim();
  if (!id || !userData?.OrganizationId) return null;

  const day = toAppointmentListDate(startTime);
  const attempts = [];
  if (day) {
    attempts.push({
      appointmentPeriod: "Custom",
      startPeriod: day,
      endPeriod: day,
      studyIds: studyId ? [studyId] : null,
    });
    if (studyId) {
      attempts.push({
        appointmentPeriod: "Custom",
        startPeriod: day,
        endPeriod: day,
        studyIds: null,
      });
    }
  }
  attempts.push({
    appointmentPeriod: "ALLTIME",
    startPeriod: "",
    endPeriod: "",
    studyIds: studyId ? [studyId] : null,
  });

  for (const attempt of attempts) {
    try {
      const payload = buildAppointmentQueryPayload({
        userData,
        actionType: "AppointmentListNew",
        ...attempt,
      });
      const { rows } = await fetchAppointments(payload);
      const match = (rows || []).find((row) =>
        sameAppointmentId(
          row?.AppointmentId ?? row?.appointmentId,
          id,
        ),
      );
      if (match) return match;
    } catch {
      /* try next window */
    }
  }
  return null;
}

export async function fetchAppointmentReasonList({
  userData,
  actionType,
}) {
  const response = await api.post(API_ENDPOINTS.EPLANNER.GET_APPOINTMENT, {
    ...baseUserFields(userData),
    ActionType: actionType,
  });
  assertApiSuccess(response, "Could not load appointment changes.");
  return {
    rows: rowsFromResponse(response, ["AppointmentList", "appointmentList"]),
    response,
  };
}

export async function fetchSingleAppointment({
  userData,
  appointmentId,
  actionType = "GetAppointmentDetails",
}) {
  const response = await api.post(API_ENDPOINTS.EPLANNER.GET_APPOINTMENT, {
    ...baseUserFields(userData),
    ActionType: actionType,
    AppointmentId: appointmentId,
  });
  assertApiSuccess(response, "Could not load appointment details.");
  return response?.data ?? response;
}

export async function appointmentActivityNew(payload) {
  const response = await api.post(
    API_ENDPOINTS.EPLANNER.APPOINTMENT_ACTIVITY_NEW,
    payload,
  );
  assertApiSuccess(response, "Appointment action failed.");
  return response;
}

/** Legacy AppointmentActivity → usp_EPlan_Appointment_NEWUI (has UpdateAppointment). */
export async function appointmentActivity(payload) {
  const response = await api.post(
    API_ENDPOINTS.EPLANNER.APPOINTMENT_ACTIVITY,
    payload,
  );
  assertApiSuccess(response, "Appointment action failed.");
  return response;
}

export async function fetchAppointmentChildDetails({ userData, appointmentId }) {
  const response = await appointmentActivityNew({
    ...baseUserFields(userData),
    ActionType: "AppointmentChildDetails",
    AppointmentId: appointmentId,
  });

  const tables = tablesFromResponse(response);
  const tableByName = (name) =>
    tables.find((table) => String(table.name).toLowerCase() === name.toLowerCase())
      ?.rows ?? [];

  const firstRow = (rows) =>
    Array.isArray(rows) && rows.length ? rows[0] : null;

  const pickRows = ({ name, shapeTest, fallbackIndex }) => {
    const named = tableByName(name);
    if (named.length) return named;
    const shaped = tables.find((table) => {
      const row = firstRow(table?.rows);
      return row && shapeTest(row);
    });
    if (shaped?.rows?.length) return shaped.rows;
    const fallback = tables[fallbackIndex]?.rows;
    return Array.isArray(fallback) ? fallback : [];
  };

  return {
    // API order: SuccessStory, Location, StaffList, StudyWorkSheetList
    staff: pickRows({
      name: "StaffList",
      fallbackIndex: 2,
      shapeTest: (row) =>
        row.StaffId != null ||
        row.staffId != null ||
        row.StaffName != null ||
        row.staffName != null,
    }),
    location: pickRows({
      name: "Location",
      fallbackIndex: 1,
      shapeTest: (row) =>
        row.LocationName != null ||
        row.locationName != null ||
        row.SiteId != null ||
        row.BuildingName != null,
    }),
    worksheets: pickRows({
      name: "StudyWorkSheetList",
      fallbackIndex: 3,
      shapeTest: (row) =>
        row.WorkSheetName != null ||
        row.workSheetName != null ||
        row.StudyWorkSheetId != null,
    }),
    response,
  };
}

/** Notes / activity entries for an appointment (BindNotes). */
export async function fetchAppointmentNotes({
  userData,
  appointmentId,
  volunteerId,
}) {
  // Legacy SP branch — BindNotes / SaveNotesNew live on AppointmentActivity.
  const response = await appointmentActivity({
    ...baseUserFields(userData),
    ActionType: "BindNotes",
    AppointmentId: idUpper(appointmentId),
    VolunteerId: idUpper(volunteerId),
  });
  const tables = tablesFromResponse(response);
  const fromTables = tables.flatMap((table) =>
    Array.isArray(table?.rows) ? table.rows : [],
  );
  if (fromTables.length) return fromTables;
  return rowsFromResponse(response, [
    "Notes",
    "notes",
    "SuccessStory",
    "successStory",
  ]);
}

/**
 * Save a note (legacy SaveNotesNew).
 * - appointment: tied to this appointment
 * - participant: volunteer-level; optional ShowInAppointments
 */
export async function saveAppointmentNote({
  userData,
  appointmentId,
  volunteerId,
  notes,
  noteLevel = "appointment",
  showInAppointments = true,
}) {
  const isParticipant = String(noteLevel || "").toLowerCase() === "participant";
  const response = await appointmentActivity({
    ...baseUserFields(userData),
    ActionType: "SaveNotesNew",
    AppointmentId: isParticipant ? "" : idUpper(appointmentId),
    VolunteerId: idUpper(volunteerId),
    Notes: String(notes || "").trim(),
    NoteShowInAppointment: isParticipant ? Boolean(showInAppointments) : false,
  });
  assertApiSuccess(response, "Could not save note.");
  return response;
}

/** Update note text (legacy EditNotes). */
export async function editAppointmentNote({
  userData,
  volunteerId,
  noteId,
  notes,
  appointmentId = "",
}) {
  const response = await appointmentActivity({
    ...baseUserFields(userData),
    ActionType: "EditNotes",
    AppointmentId: idUpper(appointmentId) || "",
    VolunteerId: idUpper(volunteerId),
    NoteId: idUpper(noteId),
    Notes: String(notes || "").trim(),
  });
  assertApiSuccess(response, "Could not update note.");
  return response;
}

/** Soft-delete note (legacy HideNotes → IsHide = 1). */
export async function hideAppointmentNote({
  userData,
  volunteerId,
  noteId,
  appointmentId = "",
}) {
  const response = await appointmentActivity({
    ...baseUserFields(userData),
    ActionType: "HideNotes",
    AppointmentId: idUpper(appointmentId) || "",
    VolunteerId: idUpper(volunteerId),
    NoteId: idUpper(noteId),
    Notes: "",
  });
  assertApiSuccess(response, "Could not delete note.");
  return response;
}

/** Book without throwing on Success 2/3 (out-of-window / study confirm flows). */
export async function bookAppointmentRequest(payload) {
  // AppointmentActivityNew → usp_EPlan_AppointmentNew_NEWUI persists GroupNameIdRef.
  // Legacy AppointmentActivity INSERT omits GroupNameIdRef (group missing after book).
  return api.post(API_ENDPOINTS.EPLANNER.APPOINTMENT_ACTIVITY_NEW, {
    ...payload,
    ActionType: payload?.ActionType || "BookAppointment",
    AppointmentStatus: payload?.AppointmentStatus || "Confirmed",
  });
}

export function appointmentNetworkErrorMessage(error, fallback) {
  const raw = error?.message || "";
  if (/failed to fetch|networkerror|request timeout/i.test(raw)) {
    return "The request timed out. The appointment may already be saved — refresh the calendar. Email, SMS and WhatsApp may still be sending.";
  }
  return raw || fallback || "Could not complete the appointment action.";
}

export function parseAppointmentActivityResult(response) {
  const data = response?.data ?? response ?? {};
  const storyRow =
    (Array.isArray(data?.SuccessStory) && data.SuccessStory[0]) ||
    (Array.isArray(data?.successStory) && data.successStory[0]) ||
    null;
  const storySuccess = Number(storyRow?.Success ?? storyRow?.success ?? NaN);
  const topSuccess = Number(response?.success ?? response?.Success ?? NaN);
  const success = Number.isFinite(storySuccess)
    ? storySuccess
    : Number.isFinite(topSuccess)
      ? topSuccess
      : utils.isApiSuccess(response)
        ? 1
        : 0;
  return {
    success,
    message: utils.getApiResponseMessage(
      response,
      success === 1 ? "Appointment booked." : "Could not book appointment.",
    ),
    story: storyRow || {},
  };
}

export async function bookAppointment(payload) {
  const response = await bookAppointmentRequest(payload);
  assertApiSuccess(response, "Appointment action failed.");
  return response;
}

/**
 * Dedicated calendar label CRUD / list (usp_EPlan_CalendarLabel_NEWUI).
 */
export async function calendarLabelActivity(payload) {
  const response = await api.post(
    API_ENDPOINTS.EPLANNER.CALENDAR_LABEL_ACTIVITY,
    payload,
  );
  return response;
}

function formatLabelDateTime(dateIso, timeHHmm = "00:00") {
  const datePart = String(dateIso || "")
    .trim()
    .slice(0, 10);
  const timePart = String(timeHHmm || "00:00")
    .trim()
    .slice(0, 5);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return "";
  const hhmm = /^\d{2}:\d{2}$/.test(timePart) ? timePart : "00:00";
  return `${datePart} ${hhmm}:00`;
}

function addOneIsoDay(dateIso) {
  const match = String(dateIso || "")
    .trim()
    .match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return "";
  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    12,
    0,
    0,
    0,
  );
  date.setDate(date.getDate() + 1);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function subtractOneIsoDay(dateIso) {
  const match = String(dateIso || "")
    .trim()
    .match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return "";
  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    12,
    0,
    0,
    0,
  );
  date.setDate(date.getDate() - 1);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** True when the calendar row is a dedicated EPlan_CalendarLabel (not a booking). */
export function isDedicatedCalendarLabel(row) {
  if (!row || typeof row !== "object") return false;
  const flag = row.IsCalendarLabel ?? row.isCalendarLabel;
  if (flag === true || flag === 1) return true;
  const text = String(flag ?? "")
    .trim()
    .toLowerCase();
  if (text === "1" || text === "true") return true;
  return Boolean(
    String(row.CalendarLabelId ?? row.calendarLabelId ?? "").trim(),
  );
}

/**
 * Build StartDateTime / EndDateTime for CreateLabel / UpdateLabel.
 * All-day: EndDateTime is exclusive (day after last inclusive date at 00:00).
 */
export function buildCalendarLabelDateTimes({
  startDateIso,
  endDateIso,
  isAllDay = true,
  startTime = "00:00",
  endTime = "23:59",
}) {
  const startDate = String(startDateIso || "").trim().slice(0, 10);
  const endDate = String(endDateIso || startDate).trim().slice(0, 10);
  if (!startDate || !endDate) {
    return { startDateTime: "", endDateTime: "" };
  }
  if (isAllDay) {
    return {
      startDateTime: formatLabelDateTime(startDate, "00:00"),
      endDateTime: formatLabelDateTime(addOneIsoDay(endDate), "00:00"),
    };
  }
  return {
    startDateTime: formatLabelDateTime(startDate, startTime),
    endDateTime: formatLabelDateTime(endDate, endTime),
  };
}

export async function fetchCalendarLabels({
  userData,
  startDate = "",
  endDate = "",
}) {
  if (!userData?.OrganizationId) return [];
  const response = await calendarLabelActivity({
    ...baseUserFields(userData),
    ActionType: "ListByRange",
    StartDate: startDate || null,
    EndDate: endDate || null,
  });
  assertApiSuccess(response, "Could not load calendar labels.");
  return rowsFromResponse(response, [
    "CalendarLabels",
    "calendarLabels",
    "Table",
    "table",
  ]);
}

/**
 * Load one dedicated calendar label (includes Comment / LabelComment).
 */
export async function fetchCalendarLabelById({ userData, calendarLabelId }) {
  const labelId = String(calendarLabelId || "")
    .trim()
    .toUpperCase();
  if (!userData?.OrganizationId || !labelId) return null;
  const response = await calendarLabelActivity({
    ...baseUserFields(userData),
    ActionType: "GetLabel",
    CalendarLabelId: labelId,
  });
  assertApiSuccess(response, "Could not load calendar label.");
  const rows = rowsFromResponse(response, [
    "CalendarLabels",
    "calendarLabels",
    "Table",
    "table",
  ]);
  return Array.isArray(rows) && rows.length ? rows[0] : null;
}

/**
 * Create a dedicated calendar label (range, colour, comment, optional times).
 */
export async function bookCalendarLabel({
  userData,
  labelName,
  startDateIso,
  endDateIso,
  isAllDay = true,
  startTime = "09:00",
  endTime = "17:00",
  color = "#f3efe6",
  textColor = "#3f3a36",
  comment = "",
  /** @deprecated legacy single-day callers */
  dateIso,
  labelDefaultTime = "00:05",
}) {
  const name = String(labelName || "").trim();
  const startDate =
    String(startDateIso || dateIso || "")
      .trim()
      .slice(0, 10) || "";
  const endDate =
    String(endDateIso || startDate || "")
      .trim()
      .slice(0, 10) || startDate;
  if (!name) {
    return { success: 0, message: "Enter a label name.", story: {} };
  }
  if (!startDate) {
    return { success: 0, message: "Select a start date.", story: {} };
  }
  if (endDate < startDate) {
    return {
      success: 0,
      message: "End date must be on or after the start date.",
      story: {},
    };
  }
  void labelDefaultTime;
  const { startDateTime, endDateTime } = buildCalendarLabelDateTimes({
    startDateIso: startDate,
    endDateIso: endDate,
    isAllDay,
    startTime,
    endTime,
  });
  if (!startDateTime || !endDateTime) {
    return { success: 0, message: "Select valid label dates.", story: {} };
  }
  const response = await calendarLabelActivity({
    ...baseUserFields(userData),
    ActionType: "CreateLabel",
    Title: name,
    StartDateTime: startDateTime,
    EndDateTime: endDateTime,
    IsAllDay: isAllDay ? 1 : 0,
    Color: color || "#f3efe6",
    TextColor: textColor || "#3f3a36",
    Comment: String(comment || "").trim() || null,
  });
  return parseAppointmentActivityResult(response);
}

/**
 * Update a dedicated calendar label (or rename-only for legacy all-day rows).
 */
export async function renameCalendarLabel({
  userData,
  appointment,
  labelName,
  startDateIso,
  endDateIso,
  isAllDay,
  startTime,
  endTime,
  color,
  textColor,
  comment,
}) {
  const name = String(labelName || "").trim();
  if (!name) {
    return { success: 0, message: "Enter a label name." };
  }

  if (!isDedicatedCalendarLabel(appointment)) {
    const appointmentId = idUpper(
      appointment?.AppointmentId ?? appointment?.appointmentId,
    );
    if (!appointmentId) {
      return { success: 0, message: "Could not find this label." };
    }
    const startRaw =
      appointment?.StartTime ??
      appointment?.startTime ??
      appointment?.start ??
      "";
    const response = await api.post(API_ENDPOINTS.EPLANNER.APPOINTMENT_ACTIVITY, {
      ...baseUserFields(userData),
      ActionType: "RenameCalendarLabel",
      AppointmentId: appointmentId,
      VolunteerName: name,
      VolunteerId: guidUpperOrEmpty(resolveVolunteerGuid(appointment)),
      AllDay: 1,
      StartTime: toLegacyAppointmentDateTime(startRaw) || null,
    });
    return parseAppointmentActivityResult(response);
  }

  const labelId = idUpper(
    appointment?.CalendarLabelId ??
      appointment?.calendarLabelId ??
      appointment?.AppointmentId ??
      appointment?.appointmentId,
  );
  if (!labelId) {
    return { success: 0, message: "Could not find this label." };
  }

  const existingStart = String(
    appointment?.StartTime ?? appointment?.startTime ?? appointment?.start ?? "",
  ).trim();
  const existingEnd = String(
    appointment?.EndTime ?? appointment?.endTime ?? appointment?.end ?? "",
  ).trim();
  const existingAllDay =
    isAllDay != null
      ? Boolean(isAllDay)
      : Number(appointment?.AllDay ?? appointment?.allDay ?? 1) === 1;

  const startDate =
    String(startDateIso || "")
      .trim()
      .slice(0, 10) || existingStart.slice(0, 10);

  let endDate = String(endDateIso || "")
    .trim()
    .slice(0, 10);
  if (!endDate) {
    const exclusiveEnd = existingEnd.slice(0, 10);
    endDate = existingAllDay
      ? subtractOneIsoDay(exclusiveEnd) || startDate
      : exclusiveEnd || startDate;
  }

  const resolvedStartTime =
    startTime ||
    (existingStart.length >= 16 ? existingStart.slice(11, 16) : "09:00");
  const resolvedEndTime =
    endTime || (existingEnd.length >= 16 ? existingEnd.slice(11, 16) : "17:00");

  const { startDateTime, endDateTime } = buildCalendarLabelDateTimes({
    startDateIso: startDate,
    endDateIso: endDate || startDate,
    isAllDay: existingAllDay,
    startTime: resolvedStartTime,
    endTime: resolvedEndTime,
  });

  const response = await calendarLabelActivity({
    ...baseUserFields(userData),
    ActionType: "UpdateLabel",
    CalendarLabelId: labelId,
    Title: name,
    StartDateTime: startDateTime,
    EndDateTime: endDateTime,
    IsAllDay: existingAllDay ? 1 : 0,
    Color:
      color ||
      appointment?.Color ||
      appointment?.color ||
      appointment?.StudySchColor ||
      "#f3efe6",
    TextColor:
      textColor ||
      appointment?.TextColor ||
      appointment?.textColor ||
      "#3f3a36",
    Comment:
      comment != null
        ? String(comment).trim() || null
        : String(
            appointment?.LabelComment ||
              appointment?.Comment ||
              appointment?.comment ||
              "",
          ).trim() || null,
  });
  return parseAppointmentActivityResult(response);
}

export async function updateCalendarLabel(payload) {
  return renameCalendarLabel(payload);
}

/**
 * Cancel dedicated label, or legacy AllDay appointment label.
 * Defined after buildAppointmentActionBase / cancelAppointment — see below.
 */
export async function cancelCalendarLabel(args) {
  return cancelCalendarLabelImpl(args);
}

export async function updateAppointment(payload) {
  // UpdateAppointment (staff / location / IDs) lives on the legacy SP only.
  // AppointmentActivityNew → usp_EPlan_AppointmentNew_NEWUI has no UpdateAppointment branch,
  // which returns an empty DataSet and surfaces as "Cannot find table 0".
  return appointmentActivity({
    ...payload,
    ActionType: payload?.ActionType || "UpdateAppointment",
  });
}

function pickUpdateScreeningId(appointment = {}) {
  return String(
    appointment?.ScreeningId ?? appointment?.screeningId ?? "",
  ).trim();
}

function pickUpdateRandomisedId(appointment = {}) {
  return String(
    appointment?.RandomisedId ??
      appointment?.randomisedId ??
      appointment?.RdId ??
      appointment?.rdId ??
      appointment?.RandomId ??
      appointment?.randomId ??
      "",
  ).trim();
}

function pickVerifyFlag(appointment = {}, keys = []) {
  for (const key of keys) {
    const value = appointment?.[key];
    if (value === true || value === false) return value;
    const text = String(value ?? "")
      .trim()
      .toLowerCase();
    if (text === "true" || text === "1") return true;
    if (text === "false" || text === "0") return false;
  }
  return false;
}

const STAFF_GUID_RE =
  /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i;

function pickStaffIdValue(staff) {
  return String(
    staff?.StaffId ??
      staff?.staffId ??
      staff?.StaffIdRef ??
      staff?.staffIdRef ??
      (typeof staff === "string" ? staff : ""),
  ).trim();
}

function isStaffGuid(value) {
  return STAFF_GUID_RE.test(String(value || "").trim());
}

/** Staff GUIDs from a list row / details payload when present. */
export function pickAppointmentStaffIds(appointment = {}) {
  if (Array.isArray(appointment?.Staffs) && appointment.Staffs.length) {
    return appointment.Staffs.map((id) => String(id || "").trim()).filter(
      isStaffGuid,
    );
  }
  const rows =
    appointment?.assignedStaff ??
    appointment?.AssignedStaff ??
    appointment?.staff ??
    appointment?.StaffList ??
    [];
  if (!Array.isArray(rows) || !rows.length) return [];
  return rows.map(pickStaffIdValue).filter(isStaffGuid);
}

function pickLocationFieldsFromRow(appointment = {}, locationRow = null) {
  const loc = locationRow || {};
  const siteId = String(
    appointment?.LocationId ??
      appointment?.locationId ??
      appointment?.LocationIdRef ??
      loc?.SiteId ??
      loc?.siteId ??
      loc?.LocationId ??
      loc?.locationId ??
      "",
  ).trim();
  const buildingId = String(
    appointment?.BuildingId ??
      appointment?.buildingId ??
      loc?.BuildingId ??
      loc?.buildingId ??
      "",
  ).trim();
  const floorId = String(
    appointment?.FloorId ??
      appointment?.floorId ??
      loc?.FloorId ??
      loc?.floorId ??
      "",
  ).trim();
  const roomId = String(
    appointment?.RoomId ??
      appointment?.roomId ??
      loc?.RoomId ??
      loc?.roomId ??
      "",
  ).trim();
  const roomTypeId = String(
    appointment?.RoomTypeId ??
      appointment?.roomTypeId ??
      appointment?.RoomCapacityIdRef ??
      loc?.TypeId ??
      loc?.typeId ??
      loc?.RoomCapacityIdRef ??
      loc?.RoomTypeId ??
      loc?.roomTypeId ??
      "",
  ).trim();
  const type = String(
    appointment?.Type ??
      appointment?.type ??
      appointment?.CapacityType ??
      loc?.TypeName ??
      loc?.typeName ??
      loc?.CapacityType ??
      loc?.capacityType ??
      "",
  ).trim();
  return {
    LocationId: siteId || null,
    LocationIdRef: siteId || null,
    BuildingId: buildingId || null,
    FloorId: floorId || null,
    RoomId: roomId || null,
    RoomTypeId: roomTypeId || null,
    Type: type || null,
  };
}

/**
 * Resolve staff / screening / random / location that partial actions must
 * re-send. Legacy Reschedule + UpdateAppointment wipe omitted StaffXML /
 * StudyVolunteer IDs.
 */
export async function resolveAppointmentPreserveFields(
  userData,
  appointment = {},
  overrides = {},
) {
  const {
    Staffs: staffOverride,
    ScreeningId: screeningOverride,
    RandomisedId: randomisedOverride,
    VerifyRandomisedId: verifyRandomOverride,
    VerifyScreeningId: verifyScreenOverride,
    LocationId: locationOverride,
    LocationIdRef: locationRefOverride,
    BuildingId: buildingOverride,
    FloorId: floorOverride,
    RoomId: roomOverride,
    RoomTypeId: roomTypeOverride,
    Type: typeOverride,
  } = overrides || {};

  const appointmentId = idUpper(
    appointment?.AppointmentId ?? appointment?.appointmentId,
  );

  let staffIds =
    staffOverride !== undefined
      ? staffOverride
      : pickAppointmentStaffIds(appointment);
  let locationFields = pickLocationFieldsFromRow(appointment);
  let details = null;

  const needsStaffDetails =
    staffOverride === undefined &&
    (!Array.isArray(staffIds) || !staffIds.length);
  const needsLocationDetails =
    roomTypeOverride === undefined &&
    locationOverride === undefined &&
    !locationFields.RoomTypeId;
  const needsChildDetails =
    Boolean(appointmentId) && (needsStaffDetails || needsLocationDetails);

  if (needsChildDetails) {
    try {
      details = await fetchAppointmentChildDetails({
        userData,
        appointmentId,
      });
    } catch {
      details = null;
    }
  }

  if (needsStaffDetails && details?.staff?.length) {
    staffIds = (Array.isArray(details.staff) ? details.staff : [])
      .map(pickStaffIdValue)
      .filter(Boolean);
  }

  if (details?.location?.[0]) {
    locationFields = {
      ...locationFields,
      ...pickLocationFieldsFromRow(appointment, details.location[0]),
    };
  }

  if (locationOverride !== undefined || locationRefOverride !== undefined) {
    const siteId = String(
      locationOverride ?? locationRefOverride ?? "",
    ).trim();
    locationFields.LocationId = siteId || null;
    locationFields.LocationIdRef = siteId || null;
  }
  if (buildingOverride !== undefined) {
    locationFields.BuildingId = String(buildingOverride || "").trim() || null;
  }
  if (floorOverride !== undefined) {
    locationFields.FloorId = String(floorOverride || "").trim() || null;
  }
  if (roomOverride !== undefined) {
    locationFields.RoomId = String(roomOverride || "").trim() || null;
  }
  if (roomTypeOverride !== undefined) {
    locationFields.RoomTypeId = String(roomTypeOverride || "").trim() || null;
  }
  if (typeOverride !== undefined) {
    locationFields.Type = String(typeOverride || "").trim() || null;
  }

  return {
    ScreeningId:
      screeningOverride !== undefined
        ? String(screeningOverride || "").trim()
        : pickUpdateScreeningId(appointment),
    RandomisedId:
      randomisedOverride !== undefined
        ? String(randomisedOverride || "").trim()
        : pickUpdateRandomisedId(appointment),
    VerifyRandomisedId:
      verifyRandomOverride !== undefined
        ? Boolean(verifyRandomOverride)
        : pickVerifyFlag(appointment, [
            "IsRandomisedIdVerify",
            "isRandomisedIdVerify",
            "VerifyRandomisedId",
            "verifyRandomisedId",
          ]),
    VerifyScreeningId:
      verifyScreenOverride !== undefined
        ? Boolean(verifyScreenOverride)
        : pickVerifyFlag(appointment, [
            "IsScreeningIdVerify",
            "isScreeningIdVerify",
            "VerifyScreeningId",
            "verifyScreeningId",
          ]),
    Staffs: Array.isArray(staffIds)
      ? staffIds.map((id) => String(id || "").trim()).filter(Boolean)
      : [],
    ...locationFields,
  };
}

/**
 * Legacy UpdateAppointment always writes Screening/Random AND resyncs staff
 * from StaffXML. Omitting either side clears it in the DB — always send both.
 */
export async function buildUpdateAppointmentPayload(
  userData,
  appointment = {},
  overrides = {},
) {
  const {
    Staffs: _staff,
    ScreeningId: _screening,
    RandomisedId: _random,
    VerifyRandomisedId: _vr,
    VerifyScreeningId: _vs,
    LocationId: _loc,
    LocationIdRef: _locRef,
    BuildingId: _b,
    FloorId: _f,
    RoomId: _r,
    RoomTypeId: _rt,
    Type: _t,
    ...restOverrides
  } = overrides || {};

  const preserved = await resolveAppointmentPreserveFields(
    userData,
    appointment,
    overrides,
  );

  return {
    ...buildAppointmentActionBase(userData, appointment),
    ActionType: "UpdateAppointment",
    ...restOverrides,
    ScreeningId: preserved.ScreeningId,
    RandomisedId: preserved.RandomisedId,
    VerifyRandomisedId: preserved.VerifyRandomisedId,
    VerifyScreeningId: preserved.VerifyScreeningId,
    Staffs: preserved.Staffs,
    // Only send location when the caller is changing it (or child detail filled RoomTypeId
    // for an explicit location override). Avoid forcing empty room updates.
    ...(overrides?.LocationId !== undefined ||
    overrides?.LocationIdRef !== undefined ||
    overrides?.RoomTypeId !== undefined
      ? {
          LocationId: preserved.LocationId,
          LocationIdRef: preserved.LocationIdRef,
          BuildingId: preserved.BuildingId,
          FloorId: preserved.FloorId,
          RoomId: preserved.RoomId,
          RoomTypeId: preserved.RoomTypeId,
          Type: preserved.Type,
        }
      : {}),
  };
}

/** UpdateAppointment that preserves Screening/Random + staff unless overridden. */
export async function updateAppointmentPreservingFields(
  userData,
  appointment,
  overrides = {},
) {
  const payload = await buildUpdateAppointmentPayload(
    userData,
    appointment,
    overrides,
  );
  return updateAppointment(payload);
}

/**
 * Change bed/chair (or site path) for an existing appointment.
 * Legacy location picker uses ReAllocation — not UpdateAppointment.
 * UpdateAppointment only UPDATEs Eplan_AppointmentLocation (no FloorIdRef,
 * no INSERT if missing) and never writes EPlan_Appointment.LocationId, so the
 * list LocationName and child-detail path stay stale.
 */
export async function reallocateAppointmentLocation(
  userData,
  appointment,
  selection = {},
) {
  const siteId = idUpper(
    selection?.locationId ??
      selection?.LocationId ??
      selection?.LocationIdRef ??
      "",
  );
  const buildingId = idUpper(
    selection?.buildingId ?? selection?.BuildingId ?? "",
  );
  const floorId = idUpper(selection?.floorId ?? selection?.FloorId ?? "");
  const roomId = idUpper(selection?.roomId ?? selection?.RoomId ?? "");
  const roomTypeId = idUpper(
    selection?.roomTypeId ?? selection?.RoomTypeId ?? "",
  );
  if (!siteId || !roomTypeId) {
    throw new Error("Site and bed/chair are required to update location.");
  }
  // Match legacy _PartialLocationPicker → AppointmentActivityNew / ReAllocation.
  return appointmentActivityNew({
    ...baseUserFields(userData),
    AppointmentId: idUpper(
      appointment?.AppointmentId ?? appointment?.appointmentId,
    ),
    LocationId: siteId,
    LocationIdRef: siteId,
    BuildingId: buildingId || null,
    FloorId: floorId || null,
    RoomId: roomId || null,
    RoomTypeId: roomTypeId,
    ActionType: "ReAllocation",
  });
}

/**
 * Reschedule payload with current staff / screening / random / location.
 * SP deletes all staff then reinserts from StaffXML; empty Screening/Random
 * inserts blank StudyVolunteer rows — always re-send existing values.
 */
export async function buildRescheduleAppointmentPayload(
  userData,
  appointment = {},
  overrides = {},
) {
  const preserved = await resolveAppointmentPreserveFields(
    userData,
    appointment,
    overrides,
  );
  const {
    Staffs: _s,
    ScreeningId: _sc,
    RandomisedId: _rd,
    VerifyRandomisedId: _vr,
    VerifyScreeningId: _vs,
    LocationId: _l,
    LocationIdRef: _lr,
    BuildingId: _b,
    FloorId: _f,
    RoomId: _r,
    RoomTypeId: _rt,
    Type: _t,
    ...restOverrides
  } = overrides || {};

  return {
    ...buildAppointmentActionBase(userData, appointment),
    ActionType: "RescheduledAppointment",
    EndTime: "False",
    ...restOverrides,
    ScreeningId: preserved.ScreeningId,
    RandomisedId: preserved.RandomisedId,
    VerifyRandomisedId: preserved.VerifyRandomisedId,
    VerifyScreeningId: preserved.VerifyScreeningId,
    Staffs: preserved.Staffs,
    LocationId: preserved.LocationId,
    LocationIdRef: preserved.LocationIdRef,
    BuildingId: preserved.BuildingId,
    FloorId: preserved.FloorId,
    RoomId: preserved.RoomId,
    RoomTypeId: preserved.RoomTypeId,
    Type: preserved.Type,
  };
}

/**
 * Build style-103 StartTime from ISO date (yyyy-MM-dd) + HH:mm[/ss][ AM|PM].
 */
export function toLegacyStartTime(dateIso, timeValue) {
  const datePart = String(dateIso || "").trim().slice(0, 10);
  const timePart = String(timeValue || "").trim();
  if (!datePart || !timePart) return "";
  return toLegacyAppointmentDateTime(`${datePart} ${timePart}`);
}

/**
 * Reschedule without throwing on Success 2/3 (confirm / study-conflict flows).
 * Caller must parse with parseAppointmentActivityResult.
 */
export async function rescheduleAppointmentRequest(payload) {
  // SP uses CONVERT(..., 103) — ISO "yyyy-MM-dd HH:mm" throws out-of-range.
  const startTime = toLegacyAppointmentDateTime(payload?.StartTime);
  const endRaw = payload?.EndTime;
  const endTime =
    endRaw === "False" || endRaw === false || endRaw == null || endRaw === ""
      ? "False"
      : toLegacyAppointmentDateTime(endRaw) || "False";
  // Use AppointmentActivity (usp_EPlan_Appointment_NEWUI) — AppointmentActivityNew's
  // Reschedule result set omits EmailId/ContactNumber and the API crashes reading them.
  return api.post(API_ENDPOINTS.EPLANNER.APPOINTMENT_ACTIVITY, {
    ...payload,
    ActionType: payload?.ActionType || "RescheduledAppointment",
    StartTime: startTime || null,
    // "False" → SP recalculates EndTime from slot / period duration.
    EndTime: endTime,
  });
}

export async function rescheduleAppointment(payload) {
  const response = await rescheduleAppointmentRequest(payload);
  const result = parseAppointmentActivityResult(response);
  if (result.success !== 1) {
    const error = new Error(
      result.message || "Could not reschedule appointment.",
    );
    error.result = result;
    error.response = response;
    throw error;
  }
  return response;
}

/**
 * Legacy CheckOutofWindowStatus: Success "0" + MSG means out of window.
 * Does not throw — Success 0 is a valid validation outcome.
 */
export async function checkOutOfWindowStatus({
  userData,
  appointmentId,
  startTime,
}) {
  const response = await api.post(API_ENDPOINTS.EPLANNER.APPOINTMENT_ACTIVITY, {
    ...baseUserFields(userData),
    ActionType: "OutOfWindowValidation",
    AppointmentId: idUpper(appointmentId),
    StartTime: toLegacyAppointmentDateTime(startTime),
  });
  const result = parseAppointmentActivityResult(response);
  const message = String(result.message || "").trim();
  // Match legacy: Success == "0" && MSG != null → out of window
  const outOfWindow = result.success === 0 && Boolean(message);
  return {
    outOfWindow,
    withinWindow: !outOfWindow,
    message,
    response,
  };
}

export async function cancelAppointment(payload) {
  return appointmentActivityNew({
    ...payload,
    ActionType: payload?.ActionType || "CancelledAppointment",
  });
}

async function cancelCalendarLabelImpl({
  userData,
  appointment,
  cancelledReason,
}) {
  const reason = String(cancelledReason || "").trim();
  if (!reason) {
    return { success: 0, message: "Cancellation reason is required." };
  }

  if (!isDedicatedCalendarLabel(appointment)) {
    const response = await cancelAppointment({
      ...buildAppointmentActionBase(userData, appointment),
      ActionType: "CancelledAppointment",
      AppointmentStatus: "Cancelled",
      CancelledReason: reason,
      ReasonForBookApp: reason,
    });
    return parseAppointmentActivityResult(response);
  }

  const labelId = idUpper(
    appointment?.CalendarLabelId ??
      appointment?.calendarLabelId ??
      appointment?.AppointmentId ??
      appointment?.appointmentId,
  );
  if (!labelId) {
    return { success: 0, message: "Could not find this label." };
  }
  const response = await calendarLabelActivity({
    ...baseUserFields(userData),
    ActionType: "CancelLabel",
    CalendarLabelId: labelId,
    CancelledReason: reason,
  });
  return parseAppointmentActivityResult(response);
}

/** Legacy status change: Arrived | NotAttempt (Did not attend). */
export async function changeAppointmentStatus(payload) {
  return appointmentActivityNew({
    ...payload,
    ActionType: payload?.ActionType || "ChangeAppStatus",
  });
}

export function buildAppointmentActionBase(userData, appointment = {}) {
  const startRaw =
    appointment?.StartTime ??
    appointment?.startTime ??
    appointment?.AppDateTime ??
    appointment?.appDateTime ??
    "";
  const endRaw = appointment?.EndTime ?? appointment?.endTime ?? "";
  const startTime = toLegacyAppointmentDateTime(startRaw);
  const endTime = toLegacyAppointmentDateTime(endRaw);
  const volunteerGuid = guidUpperOrEmpty(resolveVolunteerGuid(appointment));

  return {
    ...baseUserFields(userData),
    AppointmentId: idUpper(
      appointment?.AppointmentId ?? appointment?.appointmentId,
    ),
    VolunteerId: volunteerGuid,
    VolIdRef: volunteerGuid,
    StudyId: idUpper(
      appointment?.StudyIdRef ?? appointment?.studyIdRef ?? appointment?.StudyId,
    ),
    // Fast list uses "AdhocVisit" sentinel — only send real visit GUIDs.
    VisitId: guidUpperOrEmpty(
      appointment?.VisitIdRef ??
        appointment?.visitIdRef ??
        appointment?.VisitId ??
        appointment?.visitId,
    ),
    GroupNameIdRef: guidUpperOrEmpty(
      appointment?.GroupId ??
        appointment?.groupId ??
        appointment?.GroupNameIdRef ??
        appointment?.groupNameIdRef,
    ),
    StartTime: startTime || null,
    EndTime: endTime || null,
    AppointmentType:
      appointment?.AppointmentType ?? appointment?.appointmentType ?? null,
    AppointmentStatus:
      appointment?.AppointmentStatus ??
      appointment?.appointmentStatus ??
      "Confirmed",
    IsLinkedVolunteer: true,
  };
}

/**
 * Ensure cancel/DNA/status payloads have VolIdRef GUID (not display number).
 * Hydrates from AppointmentListNew when the row is a thin stub.
 */
export async function ensureAppointmentActionRow(userData, appointment = {}) {
  if (resolveVolunteerGuid(appointment)) return appointment;
  const appointmentId =
    appointment?.AppointmentId ?? appointment?.appointmentId ?? "";
  if (!appointmentId || !userData?.OrganizationId) return appointment;
  const full = await fetchAppointmentListRowById({
    userData,
    appointmentId,
    studyId:
      appointment?.StudyIdRef ??
      appointment?.studyIdRef ??
      appointment?.StudyId ??
      "",
    startTime:
      appointment?.StartTime ??
      appointment?.startTime ??
      appointment?.AppDateTime ??
      "",
  });
  if (!full) return appointment;
  return { ...appointment, ...full, VolIdRef: full.VolIdRef || full.volIdRef };
}

export async function fetchWindowAppointments({ userData, volunteerId, studyId }) {
  const volGuid = guidUpperOrEmpty(volunteerId);
  if (!volGuid) return [];
  const response = await api.post(API_ENDPOINTS.EPLANNER.GET_APPOINTMENT, {
    ...baseUserFields(userData),
    ActionType: "GetWindowAppointments",
    VolunteerId: volGuid,
    StudyId: idUpper(studyId),
  });
  assertApiSuccess(response, "Could not load window appointments.");
  return rowsFromResponse(response, [
    "TentativeDates",
    "tentativeDates",
    "WindowAppointments",
  ]);
}

/**
 * Visit schedule rows (StartPeriod / RefVisitId / tolerances) for smart
 * next-visit window projection in Book appointment.
 */
export async function fetchStudyVisitWindowMeta({ userData, studyId }) {
  const id = idUpper(studyId);
  if (!id || !userData?.OrganizationId) return [];
  const response = await api.post(API_ENDPOINTS.ESCHEDULE.GET_COHORT_SETUP, {
    StudyId: id,
    OrganizationId: userData.OrganizationId,
    Action: "GetCohortGroupDetails",
  });
  assertApiSuccess(response, "Could not load visit window schedule.");
  const tables = tablesFromResponse(response);
  const named = tables.find((table) =>
    /^(visit|visits)$/i.test(String(table?.name || "").trim()),
  );
  if (named?.rows?.length) return named.rows;
  if (tables[3]?.rows?.length) return tables[3].rows;
  return rowsFromResponse(response, ["Visit", "visit", "Visits", "visits"]);
}

/**
 * Staff assigned to the study (AssignStudyToUser) — same source as legacy
 * GetPlanVisitListNew / GetStaffForWindowApp StaffList.
 * Marks / sorts the study Principal Investigator when `piStaffId` is provided.
 */
export async function fetchStudyAssignedStaff({
  userData,
  studyId,
  piStaffId = "",
  piStaffName = "",
}) {
  const id = idUpper(studyId);
  if (!id || !userData?.OrganizationId) return [];
  const response = await api.post(API_ENDPOINTS.ESCHEDULE.VISITS_PER_STUDY, {
    StudyId: id,
    OrganizationId: idUpper(userData.OrganizationId),
    CreatedBy: idUpper(userData?.StaffId),
    Action: "GetStaffForWindowApp",
  });
  assertApiSuccess(response, "Could not load study staff.");
  const rows = rowsFromResponse(response, ["StaffList", "staffList"]);
  const piKey = String(piStaffId || "")
    .trim()
    .toLowerCase();
  const list = (Array.isArray(rows) ? rows : [])
    .map((row) => {
      const staffId = String(row?.StaffId ?? row?.staffId ?? "").trim();
      const staffName = String(row?.StaffName ?? row?.staffName ?? "").trim();
      if (!staffId) return null;
      const isPi = Boolean(piKey && staffId.toLowerCase() === piKey);
      return {
        StaffId: staffId,
        StaffName: staffName || staffId,
        IsPI: isPi,
        isPi,
      };
    })
    .filter(Boolean);

  if (piKey && !list.some((row) => row.StaffId.toLowerCase() === piKey)) {
    const name = String(piStaffName || "").trim();
    list.unshift({
      StaffId: String(piStaffId).trim(),
      StaffName: name || "Principal Investigator",
      IsPI: true,
      isPi: true,
    });
  }

  list.sort((a, b) => Number(Boolean(b.IsPI || b.isPi)) - Number(Boolean(a.IsPI || a.isPi)));
  return list;
}

export async function fetchVolunteerStudyAppointments({
  userData,
  volunteerId,
  studyId,
  /** When false, return every API row (old Show History). Default keeps current visit only for Booked. */
  currentVisitOnly = true,
}) {
  const volGuid = guidUpperOrEmpty(volunteerId);
  if (!volGuid) return [];
  const response = await api.post(API_ENDPOINTS.EPLANNER.GET_APPOINTMENT, {
    ...baseUserFields(userData),
    ActionType: "GetAppointmentsByStudyandVolunteer",
    VolunteerId: volGuid,
    StudyId: idUpper(studyId),
  });
  assertApiSuccess(response, "Could not load participant appointments.");
  const rows = rowsFromResponse(response, [
    "AppointmentList",
    "appointmentList",
  ]);
  if (!currentVisitOnly) {
    return sortAppointmentHistoryRowsDescending(rows);
  }
  return uniqueAppointmentRows(rows);
}

export async function fetchVolunteerStudyStatus({ userData, volunteerId }) {
  const volGuid = guidUpperOrEmpty(volunteerId);
  if (!volGuid) {
    return {
      statusList: [],
      appointmentList: [],
      currentStatus: null,
      raw: null,
      response: null,
    };
  }
  const response = await api.post(API_ENDPOINTS.EPLANNER.GET_CALENDAR_VIEW, {
    ...baseUserFields(userData),
    ActionType: "VolunteerStudyStatus",
    VisitId: null,
    GroupId: null,
    StartDate: null,
    EndDate: null,
    StudyIds: null,
    StudyId: null,
    VolunteerId: volGuid,
    IsListClicked: false,
  });
  assertApiSuccess(response, "Could not load participant studies.");
  const data = response?.data ?? {};
  return {
    statusList: rowsFromResponse(response, [
      "StatusList",
      "statusList",
      "StudyStatusList",
    ]),
    appointmentList: rowsFromResponse(response, [
      "AppointmentList",
      "appointmentList",
    ]),
    currentStatus: data.CurrentStatus ?? data.currentStatus ?? null,
    raw: data,
    response,
  };
}

export async function fetchCalendarView({
  userData,
  studyIds = null,
  locationIds = null,
  groupId = "",
  visitId = "",
  startDate = "",
  endDate = "",
  actionType = "StudyWiseView",
  isListClicked = false,
}) {
  const response = await api.post(API_ENDPOINTS.EPLANNER.GET_CALENDAR_VIEW, {
    ...baseUserFields(userData),
    StudyIds: toIdList(studyIds),
    LocationIds: toIdList(locationIds),
    // Empty string breaks uniqueidentifier SQL params — send null instead.
    GroupId: groupId ? idUpper(groupId) : null,
    VisitId: visitId ? idUpper(visitId) : null,
    VolunteerId: null,
    StudyId: null,
    StartDate: startDate || null,
    EndDate: endDate || null,
    ActionType: actionType,
    IsListClicked: Boolean(isListClicked),
  });
  assertApiSuccess(response, "Could not load calendar.");
  const data = response?.data ?? {};
  // StudyWiseView returns a bare DataTable (JSON array); VisitWiseInfo uses AppointmentsData.
  let events = [];
  if (Array.isArray(data)) {
    events = data;
  } else {
    events = rowsFromResponse(response, [
      "AppointmentsData",
      "appointmentsData",
      "Appointments",
      "appointments",
      "Events",
      "events",
      "Table",
      "table",
    ]);
  }

  // Merge dedicated calendar labels for the main calendar view.
  if (
    String(actionType || "").toLowerCase() === "studywiseview" &&
    startDate &&
    endDate
  ) {
    try {
      const labels = await fetchCalendarLabels({
        userData,
        startDate,
        endDate,
      });
      if (Array.isArray(labels) && labels.length) {
        events = [...events, ...labels];
      }
    } catch (labelError) {
      // Labels are additive — calendar appointments still load if label fetch fails.
      console.warn("Could not load calendar labels.", labelError);
    }
  }

  // Hide cancelled all-day / label rows (migrated legacy AllDay + soft-cancelled).
  // Dedicated cancelled labels are already excluded by ListByRange.
  events = (Array.isArray(events) ? events : []).filter((row) => {
    const status = String(
      row?.AppointmentStatus ?? row?.appointmentStatus ?? "",
    )
      .trim()
      .toLowerCase();
    if (status !== "cancelled") return true;
    if (isDedicatedCalendarLabel(row)) return false;
    const allDay = row?.allDay ?? row?.AllDay ?? row?.IsAllDay;
    return !(allDay === true || allDay === 1 || String(allDay) === "1");
  });

  // Keep Cancelled rows so the calendar Status filter can show/hide them.
  // Deduplicate to current visit booking only (latest SystemDateTime wins).
  return {
    events: uniqueAppointmentRows(events, { dropCancelledLatest: false }),
    groups: (!Array.isArray(data) && (data.Groups || data.groups)) || [],
    visits: (!Array.isArray(data) && (data.Visits || data.visits)) || [],
    raw: data,
    response,
  };
}

export async function searchVolunteers({
  userData,
  searchText = "",
  studyId = "",
  actionType = "SearchVolunteer",
}) {
  const response = await api.post(API_ENDPOINTS.EPLANNER.SEARCH_VOLUNTEER, {
    ...baseUserFields(userData),
    ActionType: actionType,
    SearchText: searchText,
    StudyId: studyId || "",
  });
  assertApiSuccess(response, "Participant search failed.");
  return rowsFromResponse(response, ["VolunteerList", "volunteerList"]);
}

export async function fetchInputBasedVolunteerDetails({
  userData,
  searchText = "",
  studyId = "",
}) {
  const response = await api.post(
    API_ENDPOINTS.EPLANNER.GET_INPUT_BASED_VOL_DETAILS,
    {
      ...baseUserFields(userData),
      SearchText: searchText,
      StudyId: studyId || "",
    },
  );
  assertApiSuccess(response, "Could not load participant suggestions.");
  return rowsFromResponse(response, ["VolunteerList", "volunteerList"]);
}

/**
 * Organisation sites mapped to a trial study (Tbl_StudySiteMapping),
 * further limited to sites the logged-in staff can access
 * (System Administrator / Super Admin → all study sites).
 * @param {string} trialStudyId - trialStudy.StudyId (not StudyScheduleId)
 */
export async function fetchStudyMappedOrgSites({ userData, trialStudyId }) {
  const studyId = String(trialStudyId || "").trim();
  if (!studyId) return [];

  const response = await api.post(API_ENDPOINTS.EPSF.LIST_STUDY_SITES, {
    OrganizationId: userData?.OrganizationId || null,
    StaffId: userData?.StaffId || null,
    CreatedBy: userData?.StaffId || null,
    StudyId: studyId,
  });
  assertApiSuccess(response, "Could not load study sites.");

  const tables = tablesFromResponse(response);
  let list = [];
  for (const table of tables) {
    const rows = table?.rows || [];
    if (rows.some((r) => r?.SiteId || r?.siteId)) {
      list = rows;
      break;
    }
  }
  if (!list.length) {
    const named = rowsFromResponse(response, ["Data", "data", "SuccessStory"]);
    list = named.filter((r) => r?.SiteId || r?.siteId);
  }

  const mapped = (Array.isArray(list) ? list : [])
    .map((row) => {
      const siteId = String(row?.SiteId ?? row?.siteId ?? "").trim();
      if (!siteId) return null;
      const name = String(row?.SiteName ?? row?.siteName ?? "").trim();
      const abbr = String(row?.SiteAbbr ?? row?.siteAbbr ?? "").trim();
      const siteType = String(row?.SiteType ?? row?.siteType ?? "").trim();
      const label = abbr && name ? `${abbr} — ${name}` : name || abbr || siteId;
      return {
        SiteId: siteId,
        SiteName: name,
        SiteAbbr: abbr,
        SiteType: siteType,
        label,
        value: siteId,
        row,
      };
    })
    .filter(Boolean);

  return filterStudySitesForStaffAccess(mapped, userData);
}

/** System Administrator / platform Super Admin may book at any study-mapped site. */
export function staffHasAllStudySiteAccess(userData) {
  return (
    isSystemAdministratorRole(getSessionRoleName(userData)) ||
    isSessionSuperAdmin(userData)
  );
}

/**
 * Client-side intersect of study sites with Tbl_UserSiteMapping (via staff lookup).
 * API ListStudySites already applies the same rule; this covers older backends / session MappedSiteIds.
 */
export async function filterStudySitesForStaffAccess(sites = [], userData) {
  const list = Array.isArray(sites) ? sites : [];
  if (!list.length) return [];
  if (staffHasAllStudySiteAccess(userData)) return list;

  const allowed = new Set();
  const addIds = (raw) => {
    String(raw || "")
      .split(",")
      .map((id) => id.trim().toLowerCase())
      .filter(Boolean)
      .forEach((id) => allowed.add(id));
  };

  addIds(userData?.MappedSiteIds);
  if (userData?.SiteIdRef) addIds(userData.SiteIdRef);

  if (!allowed.size && userData?.OrganizationId && userData?.StaffId) {
    try {
      const staffList = await fetchStaffLookup(userData);
      const me = (staffList || []).find(
        (row) =>
          String(row?.StaffId || "").trim().toLowerCase() ===
          String(userData.StaffId).trim().toLowerCase(),
      );
      if (me) {
        addIds(me.MappedSiteIds);
        if (me.SiteIdRef) addIds(me.SiteIdRef);
      }
    } catch {
      // API already filtered when deployed; keep server list on lookup failure.
      return list;
    }
  }

  // No mapped sites on profile → trust API result (empty or staff-scoped).
  if (!allowed.size) return list;

  return list.filter((site) =>
    allowed.has(String(site?.SiteId || site?.value || "").trim().toLowerCase()),
  );
}

export function pickDefaultStudySite(sites = []) {
  const list = Array.isArray(sites) ? sites : [];
  if (!list.length) return null;
  const hub = list.find(
    (s) => String(s.SiteType || "").trim().toLowerCase() === "hub",
  );
  return hub || list[0];
}

/**
 * Legacy location picker sites (GetLocationsByOrganizationId).
 * ViewType: Default = study-mapped sites; Custom = org-wide.
 *
 * Legacy UI clears VisitId for this call ("not mapped at visit level yet").
 * Sending VisitId with null Building/Floor hits a broken SP branch.
 */
export async function fetchAppointmentLocationSites({
  userData,
  studyId = "",
  appointmentStartTime = "",
  viewType = "Default",
}) {
  const response = await api.post(
    API_ENDPOINTS.EPLANNER.APPOINTMENT_LOCATION_ACTIVITY,
    {
      ...baseUserFields(userData),
      StudyId: idUpper(studyId) || null,
      // GUID params must be null — "" fails uniqueidentifier conversion in SQL.
      VisitId: null,
      GroupId: null,
      LocationId: null,
      BuildingId: null,
      FloorId: null,
      RoomId: null,
      RoomTypeId: null,
      VolunteerId: null,
      AppointmentStartTime: appointmentStartTime || "",
      ActionType: "GetLocationsByOrganizationId",
      ViewType: viewType || "Default",
    },
  );
  assertApiSuccess(response, "Could not load location sites.");
  // Default view may return an empty study-mapped table + a filled fallback table.
  const tables = tablesFromResponse(response);
  const named = rowsFromResponse(response, ["SuccessStory", "successStory"]);
  if (named.length) return named;
  for (let i = tables.length - 1; i >= 0; i -= 1) {
    if (tables[i]?.rows?.length) return tables[i].rows;
  }
  return [];
}

/**
 * Buildings / floors / room capacity for a site (GetDataByLocationIdV1).
 */
export async function fetchAppointmentLocationLayout({
  userData,
  studyId = "",
  locationId = "",
  appointmentStartTime = "",
  slotDuration = 0,
  viewType = "Default",
}) {
  const response = await api.post(
    API_ENDPOINTS.EPLANNER.APPOINTMENT_LOCATION_ACTIVITY,
    {
      ...baseUserFields(userData),
      StudyId: idUpper(studyId) || null,
      // Match legacy site-load: leave Visit/Group null so SP uses study/org fallbacks
      // instead of FloorId/BuildingId filters against null GUIDs.
      VisitId: null,
      GroupId: null,
      LocationId: idUpper(locationId) || null,
      BuildingId: null,
      FloorId: null,
      RoomId: null,
      RoomTypeId: null,
      VolunteerId: null,
      AppointmentStartTime: appointmentStartTime || "",
      SlotDuration: Number(slotDuration) || 0,
      ActionType: "GetDataByLocationIdV1",
      ViewType: viewType || "Default",
    },
  );
  assertApiSuccess(response, "Could not load location layout.");
  const tables = tablesFromResponse(response);
  let rooms = pickTableRows(
    tables,
    ["RoomData", "roomData", "Rooms"],
    response,
    2,
  );
  // SP may append a fallback RoomData result set after an empty mapped query.
  if (!rooms.length && tables.length > 3) {
    for (let i = tables.length - 1; i >= 2; i -= 1) {
      const name = String(tables[i]?.name || "").toLowerCase();
      if (name.includes("building") || name.includes("floor")) continue;
      if (tables[i]?.rows?.length) {
        rooms = tables[i].rows;
        break;
      }
    }
  }
  return {
    buildings: pickTableRows(tables, ["Buildings", "buildings"], response, 0),
    floors: pickTableRows(tables, ["Floors", "floors"], response, 1),
    rooms,
    response,
  };
}

/**
 * Format a Date / yyyy-MM-dd for usp_EPlanGetLocationOccupancy_NEWUI
 * (SQL convert style 103 — legacy sends DD.MM.YYYY).
 */
export function toOccupancyApiDate(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const d = String(value.getDate()).padStart(2, "0");
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const y = value.getFullYear();
    return `${d}.${m}.${y}`;
  }
  const text = String(value || "").trim();
  if (!text) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) {
    const [y, m, d] = text.slice(0, 10).split("-");
    return `${d}.${m}.${y}`;
  }
  if (/^\d{1,2}[./]\d{1,2}[./]\d{4}/.test(text)) {
    const match = text.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})/);
    if (!match) return text;
    return `${match[1].padStart(2, "0")}.${match[2].padStart(2, "0")}.${match[3]}`;
  }
  return text;
}

/** Persist preferred site/building for Location Occupancy (SaveFilter). */
export async function saveOccupancyFilter({
  userData,
  locationId,
  buildingId,
}) {
  const siteId = idUpper(locationId);
  if (!siteId) return null;
  const response = await api.post(
    API_ENDPOINTS.EPLANNER.GET_LOCATION_OCCUPANCY_DATA,
    {
      ...baseUserFields(userData),
      LocationIdRef: siteId,
      BuildingId: idUpper(buildingId) || null,
      StartDate: null,
      EndDate: null,
      ViewType: null,
      ActionType: "SaveFilter",
    },
  );
  assertApiSuccess(response, "Could not save occupancy filter.");
  return response;
}

/**
 * Buildings / floors / rooms / capacities for a site (GetBuildingsBySiteId).
 */
export async function fetchOccupancyBuildingsBySite({ userData, locationId }) {
  const siteId = idUpper(locationId);
  if (!siteId) {
    return { resources: [], response: null };
  }
  const response = await api.post(
    API_ENDPOINTS.EPLANNER.GET_LOCATION_OCCUPANCY_DATA,
    {
      ...baseUserFields(userData),
      LocationIdRef: siteId,
      BuildingId: null,
      StartDate: null,
      EndDate: null,
      ViewType: null,
      ActionType: "GetBuildingsBySiteId",
    },
  );
  assertApiSuccess(response, "Could not load buildings for site.");
  const tables = tablesFromResponse(response);
  const resources = pickTableRows(
    tables,
    ["BuildingFloorRoomsData", "buildingFloorRoomsData"],
    response,
    0,
  );
  return { resources, response };
}

/**
 * Location occupancy resource calendar (GetLocationOccupancyDataNew).
 * Returns hierarchy + appointments + blocked capacities (Day view).
 */
export async function fetchLocationOccupancy({
  userData,
  locationId = "",
  buildingId = "",
  startDate = "",
  endDate = "",
  viewType = "Day",
  actionType = "GetLocationOccupancyDataNew",
}) {
  const siteId = idUpper(locationId);
  if (!siteId) {
    return {
      resources: [],
      appointments: [],
      blocked: [],
      response: null,
    };
  }
  const start = toOccupancyApiDate(startDate);
  const end = toOccupancyApiDate(endDate || startDate);
  const response = await api.post(
    API_ENDPOINTS.EPLANNER.GET_LOCATION_OCCUPANCY_DATA,
    {
      ...baseUserFields(userData),
      LocationIdRef: siteId,
      BuildingId: idUpper(buildingId) || null,
      StartDate: start,
      EndDate: end,
      ViewType: viewType || "Day",
      ActionType: actionType,
    },
  );
  assertApiSuccess(response, "Could not load location occupancy.");
  const tables = tablesFromResponse(response);
  return {
    resources: pickTableRows(
      tables,
      ["BuildingFloorRoomsData", "buildingFloorRoomsData"],
      response,
      0,
    ),
    appointments: pickTableRows(
      tables,
      ["LocationOccupancyData", "locationOccupancyData"],
      response,
      1,
    ),
    blocked: pickTableRows(
      tables,
      ["BlockedData", "blockedData"],
      response,
      2,
    ),
    response,
  };
}

export async function fetchStudyLocationAppointments({
  userData,
  studyId,
  locationId = "",
}) {
  const response = await api.post(
    API_ENDPOINTS.EPLANNER.GET_STUDY_LOCATION_APPOINTMENTS,
    {
      ...baseUserFields(userData),
      StudyId: studyId,
      LocationId: locationId,
    },
  );
  assertApiSuccess(response, "Could not load study location appointments.");
  return rowsFromResponse(response, ["AppointmentList", "appointmentList"]);
}

export async function sendVideoConsultation(payload) {
  const response = await api.post(
    API_ENDPOINTS.EPLANNER.VIDEO_CONSULTATION,
    payload,
  );
  assertApiSuccess(response, "Video consultation request failed.");
  return response;
}

export async function sendVideoConsultationInvitation(payload) {
  const response = await api.post(
    API_ENDPOINTS.EPLANNER.VIDEO_CONSULTATION_INVITATION,
    payload,
  );
  assertApiSuccess(response, "Invitation failed.");
  return response;
}

export async function saveVirtualConsultationFeedback(payload) {
  const response = await api.post(
    API_ENDPOINTS.USER_LOGIN.SAVE_FEEDBACK,
    payload,
  );
  assertApiSuccess(response, "Could not save feedback.");
  return response;
}

export async function startVirtualConsultation(payload) {
  const response = await api.post(
    API_ENDPOINTS.USER_LOGIN.VIRTUAL_CONSULTATION,
    payload,
  );
  assertApiSuccess(response, "Could not start virtual consultation.");
  return response;
}

export function getAppointmentSuccessMessage(response) {
  return utils.getApiResponseMessage(response, "Appointment saved.");
}

function analyticsStudyIds(studyIds) {
  const list = toIdList(studyIds);
  if (!list?.length) return null;
  return list.map((id) => idUpper(id)).filter(Boolean);
}

function analyticsGroupIds(groupIds) {
  const list = toIdList(groupIds);
  if (!list?.length) return null;
  return list.map((id) => idUpper(id)).filter(Boolean);
}

function analyticsPeriodFields(dateRange = {}) {
  const period = String(dateRange.appointmentPeriod || "").trim();
  // Analytics SPs treat empty Period as All Time (no ALLTIME branch).
  if (!period || period.toUpperCase() === "ALLTIME") {
    return { Period: "", StartDate: "", EndDate: "" };
  }
  return {
    Period: period,
    StartDate: dateRange.startPeriod || "",
    EndDate: dateRange.endPeriod || "",
  };
}

/**
 * Fetch one E-Planner analytics report dataset.
 * Empty StudyIds / GroupIds / VisitIds = All (SP treats null/empty XML nodes as all).
 * Do not expand "All" into every org ID — that payload times out on Appointment Trend.
 */
export async function fetchEplannerAnalyticsReport({
  userData,
  datasetMeta,
  period = {},
  studyIds = null,
  groupIds = null,
  visitIds = null,
  allStudyIds = null,
  allGroupIds = null,
  allVisitIds = null,
}) {
  const meta = datasetMeta || {};
  // Only send IDs the user actually selected. Empty = All (server-side).
  const resolvedStudies = analyticsStudyIds(studyIds) || [];
  const resolvedGroups = analyticsGroupIds(groupIds) || [];
  const resolvedVisits = analyticsStudyIds(visitIds) || [];
  // Empty arrays still become usable empty XML via SettingsSetUp (""), which means All.
  const studyList = resolvedStudies;
  const groupList = resolvedGroups;
  const visitList = resolvedVisits;
  const periodFields = analyticsPeriodFields(period);
  const includeAdhoc =
    meta.actionType === "AppointmentAnalyticsByVisit" || !visitList.length;

  const base = {
    ...baseUserFields(userData),
    OrganizationId: idUpper(userData?.OrganizationId),
    ActionType: meta.actionType,
    StudyIds: studyList,
    GroupIds: groupList,
    VisitIds: visitList,
    AppStatus: null,
    AdhocVisit: includeAdhoc ? "ADHOCVISIT" : "",
    VisitNo: 0,
  };

  let endpoint = API_ENDPOINTS.EPLANNER.STUDY_WISE_APPOINTMENT;
  let payload = {
    ...base,
    Period: periodFields.Period,
    StartDate: periodFields.StartDate,
    EndDate: periodFields.EndDate,
  };

  if (meta.api === "getAnalytics") {
    endpoint = API_ENDPOINTS.EPLANNER.GET_ANALYTICS;
  } else if (meta.api === "ow") {
    endpoint = API_ENDPOINTS.EPLANNER.GET_OW_APP_ANALYTICS;
    // Never send "" for uniqueidentifier params — SQL conversion fails.
    const staffId =
      guidOrNull(userData?.StaffId) ||
      guidOrNull(userData?.StaffIdRef) ||
      guidOrNull(userData?.CreatedBy);
    // Study-scoped OW actions need one StudyId; org-wide GetExcel/GetCount do not.
    const needsStudyId =
      meta.actionType === "GetDetailsDataforStudyOutWindow" ||
      meta.actionType === "GetDetailsDataforStudy";
    const singleStudyId = needsStudyId
      ? guidOrNull(studyList?.[0]) || guidOrNull(allStudyIds?.[0])
      : null;
    payload = {
      OrganizationId: idUpper(userData?.OrganizationId),
      CreatedBy: staffId,
      StaffIdRef: staffId,
      ActionType: meta.actionType,
      AppointmentPeriod: periodFields.Period || "",
      StartPeriod: periodFields.StartDate || null,
      EndPeriod: periodFields.EndDate || null,
      StudyIds: studyList,
      GroupIds: groupList,
      Viewby: "Volunteers",
      StudyId: singleStudyId,
    };
  }

  let response;
  try {
    response = await api.post(endpoint, payload);
  } catch (error) {
    // Fallback if local API has not been rebuilt with ePlanner analytics actions yet.
    const settingsEndpoint =
      meta.api === "getAnalytics"
        ? "/api/Settings/GetAnalytics"
        : meta.api === "ow"
          ? "/api/Settings/GetOWAppAnalytics"
          : "/api/Settings/StudyWiseAppointment";
    response = await api.post(settingsEndpoint, payload);
  }

  // success=0 with empty tables is common; still try to read rows when present.
  if (!utils.isApiSuccess(response)) {
    const softRows = rowsFromResponse(response, [
      "SuccessStory",
      "successStory",
      "DetailsList",
    ]);
    if (!softRows.length) {
      assertApiSuccess(response, "Could not load analytics report.");
    }
  }

  const preferred = meta.detailTable
    ? [meta.detailTable, "DetailsList", "SuccessStory", "successStory"]
    : ["SuccessStory", "successStory"];
  let rows = rowsFromResponse(response, preferred);
  if (!rows.length && meta.detailTable) {
    rows = rowsFromResponse(response, ["SuccessStory", "successStory"]);
  }
  if (!rows.length) {
    const tables = tablesFromResponse(response);
    if (tables[0]?.rows?.length) rows = tables[0].rows;
  }
  return { rows, response };
}

/** WhatsApp Communication — conversation threads for current organisation. */
export async function fetchWhatsAppThreads({ userData, top = 100 } = {}) {
  const organizationId = guidOrNull(userData?.OrganizationId);
  if (!organizationId) {
    throw new Error("Organisation is required.");
  }
  const response = await api.post(API_ENDPOINTS.EPLANNER.LIST_WHATSAPP_THREADS, {
    OrganizationId: organizationId,
    StaffId: guidOrNull(userData?.StaffId) || "",
    Top: top,
  });
  assertApiSuccess(response, "Could not load WhatsApp conversations.");
  const payload = response?.data?.data ?? response?.data ?? {};
  if (Array.isArray(payload.Threads)) return payload.Threads;
  if (Array.isArray(payload.threads)) return payload.threads;
  if (Array.isArray(payload)) return payload;
  return rowsFromResponse(response, [
    "Threads",
    "threads",
    "Table",
    "table",
    "SuccessStory",
    "successStory",
  ]);
}

/** WhatsApp Communication — messages for one conversation key (E.164). */
export async function fetchWhatsAppThread({
  userData,
  conversationKey,
  top = 200,
} = {}) {
  const organizationId = guidOrNull(userData?.OrganizationId);
  if (!organizationId) {
    throw new Error("Organisation is required.");
  }
  if (!String(conversationKey || "").trim()) {
    throw new Error("Conversation is required.");
  }
  const response = await api.post(API_ENDPOINTS.EPLANNER.GET_WHATSAPP_THREAD, {
    OrganizationId: organizationId,
    StaffId: guidOrNull(userData?.StaffId) || "",
    ConversationKey: String(conversationKey).trim(),
    Top: top,
  });
  assertApiSuccess(response, "Could not load WhatsApp conversation.");
  const payload = response?.data?.data ?? response?.data ?? {};
  const messages = Array.isArray(payload.Messages)
    ? payload.Messages
    : Array.isArray(payload.messages)
      ? payload.messages
      : [];
  return {
    ConversationKey: payload.ConversationKey || payload.conversationKey || conversationKey,
    Messages: messages,
    VolunteerId: payload.VolunteerId ?? payload.volunteerId ?? null,
    ParticipantName: payload.ParticipantName ?? payload.participantName ?? "",
    VolunteerNumber: payload.VolunteerNumber ?? payload.volunteerNumber ?? "",
    response,
  };
}

/** Staff free-text WhatsApp reply (24-hour window). */
export async function replyWhatsApp({ userData, conversationKey, body } = {}) {
  const organizationId = guidOrNull(userData?.OrganizationId);
  if (!organizationId) {
    throw new Error("Organisation is required.");
  }
  const response = await api.post(API_ENDPOINTS.EPLANNER.REPLY_WHATSAPP, {
    OrganizationId: organizationId,
    ConversationKey: String(conversationKey || "").trim(),
    Body: String(body || "").trim(),
    StaffId: guidOrNull(userData?.StaffId) || "",
  });
  assertApiSuccess(response, "WhatsApp reply failed.");
  return response?.data ?? response;
}

